// 權限類型
export const PermissionTypes = {
  QUERY: 'Query',
  ADD: 'AddNew',
  EDIT: 'Edit',
  DELETE: 'Del',
  EXPORT: 'Export',
  PRINT: 'Printer',
  APPROVE: 'Approve',
} as const;

// 權限動作對應
export const PermissionActions = {
  VIEW: 'query',
  CREATE: 'add',
  UPDATE: 'edit',
  DELETE: 'delete',
  EXPORT: 'export',
  PRINT: 'print',
  APPROVE: 'approve',
} as const;

// 模組代碼 — must match SYS_Program.PgCode in database
export const Modules = {
  // 系統管理
  USER: 'USER_MGMT',           // PgId=1
  PERMISSION: 'PERMISSION_MGMT', // PgId=2
  PROGRAMS: 'PROGRAMS_MGMT',   // PgId=4

  // 業務模組
  SC_TRACKER: 'SC_TRACKER',           // PgId=9
  MANAGER_DASHBOARD: 'MANAGER_DASHBOARD', // PgId=10
  ANALYTICS: 'ANALYTICS',             // PgId=11
  AUDIT: 'AUDIT',                     // PgId=12
  MEKAK: 'MEKAK',                     // PgId=3
  DATABASE_SEARCH: 'DATABASE_SEARCH', // PgId=13
  INVENTORY_SEARCH: 'INVENTORY_SEARCH', // PgId=18
  F_TRACKER: 'F_TRACKER',             // PgId=14
  DEV_DASHBOARD: 'DEV_DASHBOARD',     // PgId=6
  FEEDBACK: 'FEEDBACK',               // PgId=5
  INBOX: 'INBOX',                     // PgId=7
  CACHE: 'CACHE_MGMT',               // PgId=8
  SYSTEM_ANNOUNCEMENTS: 'SYSTEM_ANNOUNCEMENTS', // PgId=15
  WHITEBOARD: 'WHITEBOARD',           // PgId=19
  ESP: 'ESP',                         // PgId=21, backend module Code "ESP"

  // Legacy module codes (ERP)
  SALES_CONTRACT: 'pwc202f1',
  EXCHANGE_RATE: 'fin_exchangerate',
  INVENTORY: 'INV_MAIN',
  CUSTOMER: 'CUS_MAIN',
} as const;

// 預定義權限組合
export const Permissions = {
  // 使用者管理
  USER_VIEW: `${Modules.USER}_QUERY`,
  USER_CREATE: `${Modules.USER}_ADD`,
  USER_UPDATE: `${Modules.USER}_EDIT`,
  USER_DELETE: `${Modules.USER}_DELETE`,
  
  // 權限管理
  PERMISSION_VIEW: `${Modules.PERMISSION}_QUERY`,
  PERMISSION_CREATE: `${Modules.PERMISSION}_ADD`,
  PERMISSION_UPDATE: `${Modules.PERMISSION}_EDIT`,
  PERMISSION_DELETE: `${Modules.PERMISSION}_DELETE`,
  PERMISSION_USER_UPDATE: `${Modules.PERMISSION}_USER_UPDATE`,
  
  // 庫存管理
  INVENTORY_VIEW: `${Modules.INVENTORY}_QUERY`,
  INVENTORY_CREATE: `${Modules.INVENTORY}_ADD`,
  INVENTORY_UPDATE: `${Modules.INVENTORY}_EDIT`,
  INVENTORY_DELETE: `${Modules.INVENTORY}_DELETE`,
  INVENTORY_EXPORT: `${Modules.INVENTORY}_EXPORT`,
  INVENTORY_PRINT: `${Modules.INVENTORY}_PRINT`,
  
  // 客戶管理
  CUSTOMER_VIEW: `${Modules.CUSTOMER}_QUERY`,
  CUSTOMER_CREATE: `${Modules.CUSTOMER}_ADD`,
  CUSTOMER_UPDATE: `${Modules.CUSTOMER}_EDIT`,
  CUSTOMER_DELETE: `${Modules.CUSTOMER}_DELETE`,
  CUSTOMER_EXPORT: `${Modules.CUSTOMER}_EXPORT`,
  
  // 銷售合約 (SC)
  SC_VIEW: `${Modules.SALES_CONTRACT}_QUERY`,
  SC_CREATE: `${Modules.SALES_CONTRACT}_ADD`,
  SC_UPDATE: `${Modules.SALES_CONTRACT}_EDIT`,
  SC_DELETE: `${Modules.SALES_CONTRACT}_DELETE`,
  SC_EXPORT: `${Modules.SALES_CONTRACT}_EXPORT`,
  SC_PRINT: `${Modules.SALES_CONTRACT}_PRINT`,
  SC_APPROVE: `${Modules.SALES_CONTRACT}_APPROVE`,

  // 管理員
  ADMIN: 'ADMIN',
} as const;

// 權限類型定義
export type PermissionType = typeof PermissionTypes[keyof typeof PermissionTypes];
export type PermissionAction = typeof PermissionActions[keyof typeof PermissionActions];
export type Module = typeof Modules[keyof typeof Modules];
export type Permission = typeof Permissions[keyof typeof Permissions];

// 權限檢查輔助函數
export const hasPermission = (userPermissions: string[], requiredPermission: string): boolean => {
  // 如果使用者是管理員，擁有所有權限
  if (userPermissions.includes(Permissions.ADMIN)) {
    return true;
  }
  
  // 檢查是否有指定權限
  return userPermissions.includes(requiredPermission);
};

// 檢查多個權限（任一滿足）
export const hasAnyPermission = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  return requiredPermissions.some(permission => hasPermission(userPermissions, permission));
};

// 檢查多個權限（全部滿足）
export const hasAllPermissions = (userPermissions: string[], requiredPermissions: string[]): boolean => {
  return requiredPermissions.every(permission => hasPermission(userPermissions, permission));
};

// 從模組和動作生成權限字串
export const generatePermission = (module: Module, action: PermissionAction): string => {
  const actionMap: Record<string, string> = {
    query: 'QUERY',
    add: 'ADD',
    edit: 'EDIT',
    delete: 'DELETE',
    export: 'EXPORT',
    print: 'PRINT',
    approve: 'APPROVE',
  };
  
  return `${module}_${actionMap[action]}`;
};